<template>
  <div class="contact--profile">
    <div class="contact--info">
      <h3 class="sub-block-title contact--name font-bold">
        {{ contact.name }}
      </h3>
    </div>

    <div>
      <img v-if="attachment.file_type === 'image'" :src="fileUrl" />
      <video
        v-if="attachment.file_type === 'video'"
        controls
        playsInline
        :src="fileUrl"
        @loadedmetadata="setVideoDuration($event, fileUrl)"
      />
    </div>
    <div
      class="flex justify-between items-top text-slate-600 dark:text-slate-300 text-xs me-5"
    >
      <div>
        <div v-if="attachment.file_type === 'video'">
          <div v-if="details.url">
            {{ $t('CONVERSATION_SIDEBAR.VIDEO_INFO.URL') }}:
            <a :href="details.url" target="_blank">open video</a>
          </div>
          <div>
            {{ $t('CONVERSATION_SIDEBAR.VIDEO_INFO.DURATION') }}:
            {{ videoDuration }}
          </div>
          <div>
            {{ $t('CONVERSATION_SIDEBAR.VIDEO_INFO.CREATED') }}:
            {{ videoUploadedAt }}
          </div>
        </div>
      </div>
      <div
        v-if="
          details.type === 'instagram_comment' ||
          details.type === 'facebook_comment'
        "
      >
        <fluent-icon
          v-if="details.views !== undefined && details.views !== null"
          v-tooltip="$t('CONVERSATION_SIDEBAR.INSIGHTS.VIEWS')"
          size="16"
          class="-mt-0.5 align-middle inline-block"
          icon="eye-show"
        />
        <span>
          {{ details.views }}
        </span>

        <fluent-icon
          v-if="details.likes !== undefined && details.likes !== null"
          v-tooltip="$t('CONVERSATION_SIDEBAR.INSIGHTS.LIKES')"
          size="16"
          class="-mt-0.5 align-middle inline-block ms-2"
          icon="heart"
        />
        <span>
          {{ details.likes }}
        </span>
      </div>
    </div>
    <div class="description-text" v-html="description"></div>
  </div>
</template>

<script>
import timeMixin from '../../../mixins/time';
import { containsHebrew } from 'dashboard/helper/commons';
import { format, addSeconds } from 'date-fns'; // move to timeMixin

export default {
  name: 'PostInfo',
  mixins: [timeMixin],
  props: ['chat', 'contact'],
  data() {
    return {
      videoDurations: {},
    };
  },
  computed: {
    fileUrl() {
      return this.chat?.file?.url || this.attachment.original_url;
    },
    details() {
      return this.chat?.additional_attributes || {};
    },
    attachment() {
      return this.details.attachments?.[0] || {};
    },
    description() {
      const name = this.chat?.additional_attributes?.name;
      if (!name) return '';

      return this.formatMultilineText(name);
    },
    videoDuration() {
      const seconds =
        this.attachment.duration || this.videoDurations[this.fileUrl] || 0;
      const durationDate = addSeconds(new Date(0), seconds);
      return format(durationDate, 'm:ss');
    },
    videoUploadedAt() {
      return (
        this.attachment.uploaded_at ||
        this.timestampToDate(this.chat?.created_at) ||
        ''
      );
    },
  },
  methods: {
    setVideoDuration(event, url) {
      this.$set(this.videoDurations, url, event.target.duration);
    },
    containsLetters(sentence) {
      const letterRegex = /\p{L}/u;
      return letterRegex.test(sentence);
    },
    isAllTextHebrew(sentences) {
      const letterSentences = sentences.filter(
        sentence => sentence.trim() && this.containsLetters(sentence)
      );

      if (letterSentences.length === 0) return false;

      return letterSentences.every(sentence => containsHebrew(sentence));
    },

    formatMultilineText(text) {
      const sentences = text.split('\n');
      const isTextBelongsToHebrew = this.isAllTextHebrew(sentences);

      return sentences
        .map(sentence =>
          this.wrapSentenceWithDirection(sentence, isTextBelongsToHebrew)
        )
        .join('');
    },
    wrapSentenceWithDirection(sentence, fullTextBelongToHebrew) {
      const escapedSentence = this.escapeHtml(sentence);
      let direction = containsHebrew(sentence) ? 'rtl' : 'ltr';
      let align = direction === 'rtl' ? 'right' : 'left';

      if (fullTextBelongToHebrew) {
        direction = 'rtl';
        align = 'right';
      }

      return `<div class="text-${align} direction-${direction}">${escapedSentence}</div>`;
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
  },
};
</script>

<style lang="scss" scoped>
.contact--profile {
  position: relative;
  align-items: flex-start;
  padding: var(--space-normal);
  padding-bottom: 3rem !important;

  .user-thumbnail-box {
    margin-right: var(--space-normal);
  }
}

.contact--details {
  margin-top: var(--space-small);
  width: 100%;
}

.contact--info {
  text-align: start;
  margin-bottom: 0.7rem;
}

.contact--name-wrap {
  display: flex;
  align-items: center;
  margin-bottom: var(--space-small);
}

.contact--name {
  text-transform: capitalize;
  white-space: normal;
  margin: 0 var(--space-smaller) 0 0;
}

.description-text {
  white-space: pre-wrap;
  word-wrap: break-word;

  :deep(.text-left) {
    text-align: left;
  }

  :deep(.text-right) {
    text-align: right;
  }

  :deep(.direction-ltr) {
    direction: ltr;
  }

  :deep(.direction-rtl) {
    direction: rtl;
  }
}
</style>
